<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AnalystAnswer } from '@/api/analyst'
import type { OperationsMetricId } from '@/api/snapshot'
import { METRIC_KEYS } from '@/lib/metric-format'
import TakeawayActions, { type Takeaway } from '@/components/intelligence/TakeawayActions.vue'

/**
 * The take-aways under an Elfie answer (P4 §C9.6, S1 Step 8): the ones the
 * engine prepared for it, each a person's own act (S0 §35 rule 1). Assign
 * and Share save the answer as a report first, and start from that, so
 * whoever receives it reads what was seen (Dao, 1 Oct 2026). An objective is
 * set on the answer's measure, where it is one an objective can follow.
 */
const props = defineProps<{ workspaceId: string; answer: AnalystAnswer; question: string }>()
const { t } = useI18n()

const prepared = computed(() => new Set(props.answer.actions.map((action) => action.primitive)))
const objectiveMetricId = computed(
  () =>
    props.answer.actions.find((action) => action.primitive === 'SET_AS_OBJECTIVE' && action.metricId in METRIC_KEYS)?.metricId as
      | OperationsMetricId
      | undefined,
)
const actions = computed<Takeaway[]>(() => [
  ...(prepared.value.has('ASSIGN_FOLLOW_UP') ? (['ASSIGN'] as const) : []),
  ...(prepared.value.has('SHARE_WITH_NOTE') ? (['SHARE'] as const) : []),
  ...(objectiveMetricId.value ? (['OBJECTIVE'] as const) : []),
  ...(prepared.value.has('SAVE_EXPORT_REPORT') ? (['REPORT'] as const) : []),
])
</script>

<template>
  <TakeawayActions
    v-if="actions.length > 0"
    class="answer-takeaways"
    :workspace-id="workspaceId"
    :origin="null"
    :report="{ kind: 'ANSWER', answerId: answer.answerId }"
    :context="t('actions.answer.context', { question })"
    :actions="actions"
    :objective-metric-id="objectiveMetricId"
    data-test="answer-takeaways"
  />
</template>

<style scoped>
.answer-takeaways {
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--line-subtle);
}
</style>
